import { OnDestroy, OnInit, HostListener, Component } from '@angular/core'
import { Location } from '@angular/common'
import { AppConfigService, AuthService, ImageService, UrlCollection } from '@dot-it/jml-lib'
import { filter, map, switchMap, take, tap } from 'rxjs/operators'
import { LayoutComponent } from '../layout/layout.component'
import { Md5 } from 'ts-md5'
import { Router } from '@angular/router'
import { Observable, Subscription } from 'rxjs'
import { Favorite, FavoritesService } from '../../../../projects/jml-lib/src/lib/services/favorites.service'
import { EntityLinkService } from '../../../../projects/jml-lib/src/lib/modules/app-menu/services/entity-link.service'
import { CdkDragDrop, CdkDragStart, CdkDragEnd, moveItemInArray } from '@angular/cdk/drag-drop'

@Component({
  selector: 'jml-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss']
})
export class TopBarComponent implements OnInit, OnDestroy {
  topOfPage = true

  isEditMode = false
  isDeleteMode = false
  isDragging = false

  image: any
  imageIsLoading = true
  header: string
  displayImageViewer = false
  errorOccurred = false
  imageToShow: any
  loginName: string

  favorites$: Observable<Favorite[]>

  private subscriptions: Subscription[] = []

  @HostListener('window:scroll') onScroll(e: Event) {
    this.topOfPage = document.scrollingElement.scrollTop < 20
  }

  constructor(
    public app: LayoutComponent,
    public appConfigService: AppConfigService,
    public authService: AuthService,
    private imageService: ImageService,
    private favoritesService: FavoritesService,
    private router: Router,
    private location: Location
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.authService.localUser$
        .pipe(
          filter((user) => !!user),
          take(1)
        )
        .subscribe((user) => {
          this.loginName = user?.user?.loginName
          // takes care of showing favs in topbar
          this.favoritesService.loadFavorites()
        })
    )

    this.loadPicture()
    this.favorites$ = this.favoritesService.favorites$.pipe(
      // TODO: This is a quick hack to overcome the "Expression has changed.." error. We need to refactor the whole URL building pattern
      map((favorites) => {
        favorites.forEach((favorite) => (favorite.resolvedURL$ = this.getResolvedUrl$(favorite)))
        return favorites
      })
    )
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe())
  }

  public getGravatarLink$() {
    return this.authService.localUser$.pipe(
      take(1),
      map((user) => {
        const email = user?.user?.email ? user.user.email : ' '
        return 'https://gravatar.com/avatar/' + <string>Md5.hashStr(email.trim().toLowerCase())
      })
    )
  }

  navigateToFavorite(favorite: Favorite) {
    this.favoritesService.navigateToFavorite(favorite)
  }

  loadPicture() {
    this.imageIsLoading = true

    this.imageService.getAppLogo$().subscribe(
      (imgStr) => {
        this.imageToShow = imgStr
        this.imageIsLoading = false
        this.errorOccurred = false
      },
      (err) => {
        console.log('[PictureDialogComponent-loadPicture] error loading image: ', err)
        this.imageIsLoading = false
        this.errorOccurred = true
      }
    )
  }

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode
    if (this.isDeleteMode) {
      this.isDeleteMode = false
    }
    document.querySelector('.edit-mode-toggle')?.classList.toggle('active', this.isEditMode)
  }

  toggleDeleteMode(): void {
    this.isDeleteMode = !this.isDeleteMode
    document.querySelector('.delete-mode-toggle')?.classList.toggle('active', this.isDeleteMode)
  }

  removeFavorite(event: Event, id: string): void {
    // prevent favorites to be clickable
    event.stopPropagation()

    this.favoritesService.removeFavorite(id)

    // check if favorites are empty
    this.favorites$.pipe(take(1)).subscribe((favorites) => {
      if (favorites.length === 0) {
        // if favorites are empty, disable edit- and delete-mode
        this.isDeleteMode = false
        this.isEditMode = false
      }
    })
  }

  onDrop(event: CdkDragDrop<Favorite[]>): void {
    // get list of current favorites
    this.favorites$.pipe(take(1)).subscribe((favorites) => {
      const updatedFavorites = [...favorites]
      moveItemInArray(updatedFavorites, event.previousIndex, event.currentIndex)
      this.favoritesService.reorderFavorites(updatedFavorites)
    })
  }

  onDragStarted(event: CdkDragStart) {
    document.body.style.cursor = 'grabbing'
    document.body.classList.add('dragging-active')
    this.isDragging = true
  }

  onDragEnded(event: CdkDragEnd) {
    document.body.style.cursor = ''
    document.body.classList.remove('dragging-active')
    this.isDragging = false
  }

  getResolvedUrl$(favorite: Favorite): Observable<string> {
    return this.favoritesService.getResolvedUrl$(favorite).pipe(
      map((route) => {
        // clean up route - remove any protocol/origin if present
        let cleanRoute = route

        // ff it's a full URL, extract just the path part
        if (route.includes('://')) {
          try {
            const url = new URL(route)
            cleanRoute = url.pathname + url.search + url.hash
          } catch (e) {
            console.warn('Could not parse URL:', route)
            cleanRoute = route
          }
        }

        // ensure it starts with /
        if (!cleanRoute.startsWith('/')) {
          cleanRoute = `/${cleanRoute}`
        }

        // add hash prefix
        return `#${cleanRoute}`
      })
    )
  }

  handleFavoriteClick(event: MouseEvent, favorite: Favorite): void {
    // only prevent default and use SPA navigation for left clicks
    // right and middle clicks should use the href naturally
    if (event.button === 0 && !event.ctrlKey && !event.metaKey) {
      // left click without modifier keys - use SPA navigation
      if (!this.isEditMode && !this.isDeleteMode) {
        event.preventDefault()
        this.navigateToFavorite(favorite)
      }
    }
  }
}
